import React, { useState, useEffect, useCallback } from 'react';

const ESCROW_WALLET = 'CPnxBxM6jdkU4iSm4spo7ydHAEnphk38gz7EuSkC6Ke3';
const ADMIN_WALLET = 'CPnxBxM6jdkU4iSm4spo7ydHAEnphk38gz7EuSkC6Ke3';

// Storage keys
const STORAGE_KEYS = {
  services: 'quid_services',
  payments: 'quid_payments',
  chats: 'quid_chats',
  purchased: 'quid_purchased'
};

export default function Quid() {
  const [services, setServices] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showChatModal, setShowChatModal] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [chatService, setChatService] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState('idle');
  const [walletAddress, setWalletAddress] = useState('');
  const [walletType, setWalletType] = useState('');
  const [txSignature, setTxSignature] = useState('');
  const [purchasedServices, setPurchasedServices] = useState([]);
  const [pendingPayments, setPendingPayments] = useState([]);
  const [isConnecting, setIsConnecting] = useState(false);
  const [showWalletMenu, setShowWalletMenu] = useState(false);
  const [connectionError, setConnectionError] = useState('');
  const [chatMessages, setChatMessages] = useState({});
  const [newMessage, setNewMessage] = useState('');
  const [newService, setNewService] = useState({
    title: '', description: '', price: '', category: 'development', seller: '', sellerWallet: '', image: ''
  });
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [adminTab, setAdminTab] = useState('pending');

  // Load data from persistent storage on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        const servicesData = await window.storage.get(STORAGE_KEYS.services, true);
        if (servicesData?.value) {
          setServices(JSON.parse(servicesData.value));
        }
        
        const paymentsData = await window.storage.get(STORAGE_KEYS.payments, true);
        if (paymentsData?.value) {
          setPendingPayments(JSON.parse(paymentsData.value));
        }
        
        const chatsData = await window.storage.get(STORAGE_KEYS.chats, true);
        if (chatsData?.value) {
          setChatMessages(JSON.parse(chatsData.value));
        }
      } catch (e) {
        console.log('Storage not available or empty');
      }
    };
    loadData();
  }, []);

  // Load user-specific purchased services
  useEffect(() => {
    if (walletAddress) {
      const loadPurchased = async () => {
        try {
          const purchasedData = await window.storage.get(`${STORAGE_KEYS.purchased}_${walletAddress}`, false);
          if (purchasedData?.value) {
            setPurchasedServices(JSON.parse(purchasedData.value));
          }
        } catch (e) {
          console.log('No purchased data');
        }
      };
      loadPurchased();
    }
  }, [walletAddress]);

  // Save services to shared storage
  const saveServices = useCallback(async (newServices) => {
    try {
      await window.storage.set(STORAGE_KEYS.services, JSON.stringify(newServices), true);
    } catch (e) {
      console.log('Could not save services');
    }
  }, []);

  // Save payments to shared storage
  const savePayments = useCallback(async (newPayments) => {
    try {
      await window.storage.set(STORAGE_KEYS.payments, JSON.stringify(newPayments), true);
    } catch (e) {
      console.log('Could not save payments');
    }
  }, []);

  // Save chats to shared storage
  const saveChats = useCallback(async (newChats) => {
    try {
      await window.storage.set(STORAGE_KEYS.chats, JSON.stringify(newChats), true);
    } catch (e) {
      console.log('Could not save chats');
    }
  }, []);

  // Save purchased to personal storage
  const savePurchased = useCallback(async (newPurchased) => {
    if (walletAddress) {
      try {
        await window.storage.set(`${STORAGE_KEYS.purchased}_${walletAddress}`, JSON.stringify(newPurchased), false);
      } catch (e) {
        console.log('Could not save purchased');
      }
    }
  }, [walletAddress]);

  // Poll for updates every 3 seconds
  useEffect(() => {
    const pollUpdates = async () => {
      try {
        const servicesData = await window.storage.get(STORAGE_KEYS.services, true);
        if (servicesData?.value) {
          const parsed = JSON.parse(servicesData.value);
          setServices(prev => {
            if (JSON.stringify(prev) !== JSON.stringify(parsed)) {
              return parsed;
            }
            return prev;
          });
        }
        
        const paymentsData = await window.storage.get(STORAGE_KEYS.payments, true);
        if (paymentsData?.value) {
          const parsed = JSON.parse(paymentsData.value);
          setPendingPayments(prev => {
            if (JSON.stringify(prev) !== JSON.stringify(parsed)) {
              return parsed;
            }
            return prev;
          });
        }
        
        const chatsData = await window.storage.get(STORAGE_KEYS.chats, true);
        if (chatsData?.value) {
          const parsed = JSON.parse(chatsData.value);
          setChatMessages(prev => {
            if (JSON.stringify(prev) !== JSON.stringify(parsed)) {
              return parsed;
            }
            return prev;
          });
        }
      } catch (e) {}
    };
    
    const interval = setInterval(pollUpdates, 3000);
    return () => clearInterval(interval);
  }, []);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('Image must be under 5MB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewService(prev => ({ ...prev, image: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  useEffect(() => {
    const checkConnection = async () => {
      try {
        if (window?.phantom?.solana?.isConnected) {
          const resp = await window.phantom.solana.connect({ onlyIfTrusted: true });
          setWalletAddress(resp.publicKey.toString());
          setWalletType('phantom');
        } else if (window?.solflare?.isConnected) {
          setWalletAddress(window.solflare.publicKey.toString());
          setWalletType('solflare');
        }
      } catch (e) {}
    };
    setTimeout(checkConnection, 100);
  }, []);

  const getProvider = (type) => {
    if (type === 'phantom') {
      if (window?.phantom?.solana) return window.phantom.solana;
      window.open('https://phantom.app/', '_blank');
    } else if (type === 'solflare') {
      if (window?.solflare) return window.solflare;
      window.open('https://solflare.com/', '_blank');
    } else if (type === 'backpack') {
      if (window?.backpack) return window.backpack;
      window.open('https://backpack.app/', '_blank');
    }
    return null;
  };

  const connectWallet = async (type) => {
    setIsConnecting(true);
    setConnectionError('');
    setShowWalletMenu(false);
    try {
      const provider = getProvider(type);
      if (!provider) {
        setConnectionError(`${type} not found. Opening download...`);
        setIsConnecting(false);
        return;
      }
      let publicKey;
      if (type === 'phantom') {
        const response = await provider.connect();
        publicKey = response.publicKey.toString();
      } else if (type === 'solflare') {
        await provider.connect();
        publicKey = provider.publicKey.toString();
      } else if (type === 'backpack') {
        const response = await provider.connect();
        publicKey = response.publicKey.toString();
      }
      if (publicKey) {
        setWalletAddress(publicKey);
        setWalletType(type);
        setNewService(prev => ({ ...prev, sellerWallet: publicKey }));
      }
    } catch (error) {
      setConnectionError(error.code === 4001 ? 'Rejected by user' : (error.message || 'Connection failed'));
    } finally {
      setIsConnecting(false);
    }
  };

  const disconnectWallet = async () => {
    try {
      const provider = getProvider(walletType);
      if (provider?.disconnect) await provider.disconnect();
    } catch (e) {}
    setWalletAddress('');
    setWalletType('');
    setShowWalletMenu(false);
    setPurchasedServices([]);
  };

  const categories = [
    { id: 'all', label: 'ALL' }, { id: 'development', label: 'DEV' },
    { id: 'design', label: 'DESIGN' }, { id: 'writing', label: 'WRITING' },
    { id: 'consulting', label: 'CONSULT' }, { id: 'other', label: 'OTHER' }
  ];

  const handleCreateService = () => {
    const wallet = newService.sellerWallet || walletAddress;
    if (!newService.title.trim() || !newService.description.trim() || 
        !newService.price || !newService.seller.trim() || !wallet.trim()) {
      alert('Please fill all fields');
      return;
    }
    const service = {
      id: Date.now(),
      title: newService.title.trim(),
      description: newService.description.trim(),
      price: parseFloat(newService.price).toFixed(2),
      category: newService.category,
      seller: newService.seller.trim(),
      sellerWallet: wallet.trim(),
      image: newService.image || '',
      createdAt: new Date().toLocaleDateString('en-US', { year: '2-digit', month: '2-digit', day: '2-digit' })
    };
    const newServices = [service, ...services];
    setServices(newServices);
    saveServices(newServices);
    
    const newChats = { ...chatMessages, [service.id]: [] };
    setChatMessages(newChats);
    saveChats(newChats);
    
    setNewService({ title: '', description: '', price: '', category: 'development', seller: '', sellerWallet: walletAddress || '', image: '' });
    setShowCreateForm(false);
  };

  const handleDeleteService = (id) => {
    const newServices = services.filter(s => s.id !== id);
    setServices(newServices);
    saveServices(newServices);
  };

  const handlePurchaseClick = (service) => {
    setSelectedService(service);
    setPaymentStatus('idle');
    setTxSignature('');
    setShowPaymentModal(true);
  };

  const handlePaymentSubmit = () => {
    if (!txSignature.trim()) return;
    setPaymentStatus('pending');
    
    const payment = {
      id: Date.now(),
      serviceId: selectedService.id,
      serviceTitle: selectedService.title,
      amount: selectedService.price,
      buyerWallet: walletAddress,
      sellerWallet: selectedService.sellerWallet,
      txSignature: txSignature.trim(),
      status: 'pending',
      createdAt: new Date().toLocaleString()
    };
    const newPayments = [...pendingPayments, payment];
    setPendingPayments(newPayments);
    savePayments(newPayments);
    
    setTimeout(() => {
      setPaymentStatus('confirmed');
      const newPurchased = [...purchasedServices, selectedService.id];
      setPurchasedServices(newPurchased);
      savePurchased(newPurchased);
    }, 2000);
  };

  const openChat = (service) => {
    if (!walletAddress) {
      alert('Please connect your wallet to chat');
      return;
    }
    const isBuyer = purchasedServices.includes(service.id);
    const isSeller = service.sellerWallet === walletAddress;
    if (!isBuyer && !isSeller) {
      alert('You must purchase this service or be the seller to access chat');
      return;
    }
    setChatService(service);
    setShowChatModal(true);
  };

  const sendMessage = () => {
    if (!newMessage.trim() || !chatService) return;
    const message = {
      id: Date.now(),
      sender: walletAddress,
      text: newMessage.trim(),
      timestamp: new Date().toLocaleTimeString()
    };
    const newChats = {
      ...chatMessages,
      [chatService.id]: [...(chatMessages[chatService.id] || []), message]
    };
    setChatMessages(newChats);
    saveChats(newChats);
    setNewMessage('');
  };

  const releasePayment = (paymentId) => {
    const newPayments = pendingPayments.map(p => 
      p.id === paymentId ? { ...p, status: 'released' } : p
    );
    setPendingPayments(newPayments);
    savePayments(newPayments);
  };

  const refundPayment = (paymentId) => {
    const newPayments = pendingPayments.map(p => 
      p.id === paymentId ? { ...p, status: 'refunded' } : p
    );
    setPendingPayments(newPayments);
    savePayments(newPayments);
  };

  const isAdmin = walletAddress === ADMIN_WALLET;
  const filteredServices = selectedCategory === 'all' ? services : services.filter(s => s.category === selectedCategory);
  const truncate = (addr) => addr?.length > 12 ? `${addr.slice(0, 4)}...${addr.slice(-4)}` : addr;

  const colors = {
    primary: '#1a56db',
    primaryDark: '#1e40af',
    primaryLight: '#3b82f6',
    accent: '#0ea5e9',
    bg: '#ffffff',
    bgAlt: '#f8fafc',
    bgDark: '#e2e8f0',
    border: '#cbd5e1',
    borderDark: '#94a3b8',
    text: '#1e293b',
    textLight: '#64748b',
    success: '#059669',
    warning: '#d97706',
    danger: '#dc2626'
  };

  const styles = {
    container: { minHeight: '100vh', backgroundColor: colors.bgAlt, fontFamily: '"IBM Plex Mono", "Courier New", monospace', padding: 0 },
    header: { backgroundColor: colors.primary, borderBottom: `4px solid ${colors.primaryDark}`, padding: '16px 24px' },
    headerInner: { maxWidth: '1200px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' },
    title: { fontSize: '28px', color: '#fff', margin: 0, letterSpacing: '2px', fontWeight: 'bold' },
    subtitle: { color: '#bfdbfe', fontSize: '13px', margin: '4px 0 0' },
    btn: { padding: '10px 20px', fontSize: '14px', cursor: 'pointer', border: 'none', borderRadius: '6px', fontWeight: '600', transition: 'all 0.2s' },
    primaryBtn: { backgroundColor: '#fff', color: colors.primary },
    blueBtn: { backgroundColor: colors.primaryLight, color: '#fff' },
    successBtn: { backgroundColor: colors.success, color: '#fff' },
    dangerBtn: { backgroundColor: colors.danger, color: '#fff' },
    main: { maxWidth: '1200px', margin: '0 auto', padding: '24px' },
    nav: { backgroundColor: '#fff', border: `1px solid ${colors.border}`, borderRadius: '8px', padding: '16px', marginBottom: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
    catBtn: { padding: '8px 16px', fontSize: '12px', cursor: 'pointer', border: `2px solid ${colors.border}`, borderRadius: '20px', marginRight: '8px', marginBottom: '8px', backgroundColor: '#fff', color: colors.text, transition: 'all 0.2s' },
    catBtnActive: { backgroundColor: colors.primary, color: '#fff', borderColor: colors.primary },
    statusBar: { backgroundColor: '#fff', border: `1px solid ${colors.border}`, borderRadius: '8px', padding: '12px 16px', marginBottom: '24px', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
    grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px' },
    card: { backgroundColor: '#fff', border: `1px solid ${colors.border}`, borderRadius: '12px', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', transition: 'transform 0.2s, box-shadow 0.2s' },
    cardHeader: { backgroundColor: colors.primary, padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    cardBody: { padding: '16px' },
    cardImage: { width: '100%', height: '160px', objectFit: 'cover' },
    modal: { position: 'fixed', inset: 0, backgroundColor: 'rgba(15,23,42,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: '24px', backdropFilter: 'blur(4px)' },
    modalContent: { backgroundColor: '#fff', border: `1px solid ${colors.border}`, borderRadius: '16px', maxWidth: '500px', width: '100%', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 25px 50px rgba(0,0,0,0.25)' },
    modalHeader: { backgroundColor: colors.primary, padding: '16px 20px', borderRadius: '16px 16px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    input: { width: '100%', backgroundColor: colors.bgAlt, color: colors.text, border: `2px solid ${colors.border}`, borderRadius: '8px', padding: '12px', fontSize: '14px', fontFamily: 'inherit', transition: 'border-color 0.2s' },
    label: { display: 'block', color: colors.text, fontSize: '13px', marginBottom: '6px', fontWeight: '600' },
    footer: { backgroundColor: colors.primary, padding: '20px', marginTop: '48px', textAlign: 'center' },
    chatContainer: { height: '300px', overflowY: 'auto', backgroundColor: colors.bgAlt, border: `1px solid ${colors.border}`, borderRadius: '8px', padding: '12px', marginBottom: '12px' },
    chatMessage: { marginBottom: '10px', padding: '10px 14px', borderRadius: '12px' },
    chatInput: { display: 'flex', gap: '8px' }
  };

  return (
    <div style={styles.container}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500;600;700&display=swap');
        @keyframes blink { 0%,50%{opacity:1} 51%,100%{opacity:0} }
        *{box-sizing:border-box}
        ::selection{background:${colors.primaryLight};color:#fff}
        ::-webkit-scrollbar{width:8px}
        ::-webkit-scrollbar-track{background:${colors.bgAlt}}
        ::-webkit-scrollbar-thumb{background:${colors.border};border-radius:4px}
        ::-webkit-scrollbar-thumb:hover{background:${colors.borderDark}}
        input:focus,textarea:focus,select:focus{outline:none;border-color:${colors.primary}!important}
      `}</style>

      <header style={styles.header}>
        <div style={styles.headerInner}>
          <div>
            <h1 style={styles.title}>◎ QUID</h1>
            <p style={styles.subtitle}>SOLANA SERVICE MARKETPLACE<span style={{animation:'blink 1s infinite',marginLeft:'4px'}}>_</span></p>
          </div>
          <div style={{display:'flex',gap:'10px',alignItems:'center',flexWrap:'wrap'}}>
            {isAdmin && (
              <button onClick={()=>setShowAdminPanel(true)} style={{...styles.btn,...styles.dangerBtn,fontSize:'12px',padding:'8px 14px'}}>
                🔐 ESCROW PANEL
              </button>
            )}
            <div style={{position:'relative'}}>
              {walletAddress ? (
                <button onClick={()=>setShowWalletMenu(!showWalletMenu)} style={{...styles.btn,backgroundColor:'rgba(255,255,255,0.15)',color:'#fff',display:'flex',alignItems:'center',gap:'8px',border:'1px solid rgba(255,255,255,0.3)'}}>
                  <span style={{width:'8px',height:'8px',backgroundColor:'#4ade80',borderRadius:'50%'}}/>
                  {truncate(walletAddress)}
                </button>
              ) : (
                <button onClick={()=>setShowWalletMenu(!showWalletMenu)} disabled={isConnecting} style={{...styles.btn,...styles.primaryBtn}}>
                  {isConnecting ? 'CONNECTING...' : '◎ CONNECT'}
                </button>
              )}
              {showWalletMenu && (
                <div style={{position:'absolute',top:'100%',right:0,marginTop:'8px',backgroundColor:'#fff',border:`1px solid ${colors.border}`,borderRadius:'12px',minWidth:'220px',zIndex:100,boxShadow:'0 10px 40px rgba(0,0,0,0.15)',overflow:'hidden'}}>
                  {walletAddress ? (
                    <>
                      <div style={{padding:'14px',borderBottom:`1px solid ${colors.border}`,backgroundColor:colors.bgAlt}}>
                        <div style={{color:colors.textLight,fontSize:'10px',textTransform:'uppercase',letterSpacing:'1px'}}>Connected via {walletType}</div>
                        <div style={{color:colors.text,fontSize:'12px',wordBreak:'break-all',marginTop:'4px',fontWeight:'600'}}>{walletAddress}</div>
                      </div>
                      <button onClick={disconnectWallet} style={{width:'100%',background:'transparent',color:colors.danger,border:'none',padding:'14px',cursor:'pointer',textAlign:'left',fontSize:'13px',fontWeight:'600'}}>✕ Disconnect</button>
                    </>
                  ) : (
                    <>
                      <div style={{padding:'10px 14px',borderBottom:`1px solid ${colors.border}`,color:colors.textLight,fontSize:'11px',textTransform:'uppercase',letterSpacing:'1px'}}>Select Wallet</div>
                      {connectionError && <div style={{padding:'10px 14px',backgroundColor:'#fef2f2',color:colors.danger,fontSize:'12px'}}>{connectionError}</div>}
                      {[{id:'phantom',name:'Phantom',color:'#ab9ff2',icon:'👻'},{id:'solflare',name:'Solflare',color:'#fc8c03',icon:'🔥'},{id:'backpack',name:'Backpack',color:'#e33e3f',icon:'🎒'}].map(w=>(
                        <button key={w.id} onClick={()=>connectWallet(w.id)} style={{width:'100%',background:'transparent',color:colors.text,border:'none',padding:'14px',cursor:'pointer',textAlign:'left',fontSize:'13px',display:'flex',alignItems:'center',gap:'10px',transition:'background 0.2s'}}
                          onMouseOver={e=>e.currentTarget.style.backgroundColor=colors.bgAlt}
                          onMouseOut={e=>e.currentTarget.style.backgroundColor='transparent'}>
                          <span style={{fontSize:'18px'}}>{w.icon}</span> {w.name}
                        </button>
                      ))}
                    </>
                  )}
                </div>
              )}
            </div>
            <button onClick={()=>{
              if (!walletAddress) {
                alert('Please connect your wallet to create a service');
                return;
              }
              setNewService({title:'',description:'',price:'',category:'development',seller:'',sellerWallet:walletAddress,image:''});
              setShowCreateForm(true);
            }} style={{...styles.btn,...styles.primaryBtn, opacity: walletAddress ? 1 : 0.7}}>+ NEW SERVICE</button>
          </div>
        </div>
      </header>

      <main style={styles.main}>
        <nav style={styles.nav}>
          <div style={{color:colors.textLight,fontSize:'11px',marginBottom:'12px',textTransform:'uppercase',letterSpacing:'1px'}}>Filter by Category</div>
          <div>{categories.map(c=>(
            <button key={c.id} onClick={()=>setSelectedCategory(c.id)} style={{...styles.catBtn,...(selectedCategory===c.id?styles.catBtnActive:{})}}>{c.label}</button>
          ))}</div>
        </nav>

        <div style={styles.statusBar}>
          <span style={{color:colors.text,fontSize:'13px'}}>
            <span style={{color:colors.primary,fontWeight:'600'}}>{filteredServices.length}</span> Services 
            <span style={{margin:'0 12px',color:colors.border}}>│</span>
            Wallet: <span style={{color:walletAddress?colors.success:colors.textLight,fontWeight:'600'}}>{walletAddress?'Connected':'Not Connected'}</span>
          </span>
          <span style={{color:colors.textLight,fontSize:'13px'}}>Network: <span style={{color:colors.primary,fontWeight:'600'}}>Solana</span> • <span style={{color:colors.success}}>Live Sync</span></span>
        </div>

        {filteredServices.length === 0 ? (
          <div style={{backgroundColor:'#fff',border:`2px dashed ${colors.border}`,borderRadius:'16px',padding:'60px',textAlign:'center'}}>
            <div style={{fontSize:'48px',marginBottom:'16px'}}>📭</div>
            <h3 style={{color:colors.text,fontSize:'20px',margin:'0 0 8px'}}>No Services Yet</h3>
            <p style={{color:colors.textLight,margin:'0 0 20px'}}>Be the first to create a service listing!</p>
            <button onClick={()=>{
              if (!walletAddress) {
                alert('Please connect your wallet to create a service');
                return;
              }
              setNewService({title:'',description:'',price:'',category:'development',seller:'',sellerWallet:walletAddress,image:''});
              setShowCreateForm(true);
            }} style={{...styles.btn,...styles.blueBtn}}>+ Create Service</button>
          </div>
        ) : (
          <div style={styles.grid}>
            {filteredServices.map(s=>(
              <div key={s.id} style={styles.card} onMouseOver={e=>{e.currentTarget.style.transform='translateY(-4px)';e.currentTarget.style.boxShadow='0 12px 24px rgba(0,0,0,0.15)'}} onMouseOut={e=>{e.currentTarget.style.transform='translateY(0)';e.currentTarget.style.boxShadow='0 2px 8px rgba(0,0,0,0.08)'}}>
                <div style={styles.cardHeader}>
                  <span style={{color:'#fff',fontSize:'11px',textTransform:'uppercase',letterSpacing:'1px'}}>{s.category}</span>
                  {s.sellerWallet === walletAddress && (
                    <button onClick={()=>handleDeleteService(s.id)} style={{background:'rgba(255,255,255,0.2)',border:'none',color:'#fff',width:'24px',height:'24px',borderRadius:'6px',cursor:'pointer',fontSize:'12px'}}>✕</button>
                  )}
                </div>
                {s.image && <img src={s.image} alt={s.title} style={styles.cardImage}/>}
                <div style={styles.cardBody}>
                  <h3 style={{color:colors.text,fontSize:'18px',margin:'0 0 8px',fontWeight:'700'}}>{s.title}</h3>
                  <p style={{color:colors.textLight,fontSize:'13px',marginBottom:'16px',minHeight:'50px',lineHeight:'1.5'}}>{s.description}</p>
                  <div style={{borderTop:`1px solid ${colors.border}`,paddingTop:'14px'}}>
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'10px'}}>
                      <span style={{color:colors.primary,fontSize:'24px',fontWeight:'700'}}>◎ {s.price}</span>
                      <span style={{color:colors.textLight,fontSize:'11px'}}>{s.createdAt}</span>
                    </div>
                    <div style={{color:colors.text,fontSize:'12px',fontWeight:'600'}}>{s.seller}</div>
                    <div style={{color:colors.textLight,fontSize:'10px',marginTop:'2px'}}>{truncate(s.sellerWallet)}</div>
                  </div>
                  <div style={{display:'flex',gap:'8px',marginTop:'16px'}}>
                    {purchasedServices.includes(s.id) ? (
                      <>
                        <button style={{flex:1,...styles.btn,...styles.successBtn,fontSize:'12px',padding:'10px'}}>✓ Owned</button>
                        <button onClick={()=>openChat(s)} style={{...styles.btn,...styles.blueBtn,fontSize:'12px',padding:'10px'}}>💬</button>
                      </>
                    ) : s.sellerWallet === walletAddress ? (
                      <>
                        <button style={{flex:1,...styles.btn,backgroundColor:colors.bgAlt,color:colors.text,fontSize:'12px',padding:'10px'}}>Your Service</button>
                        <button onClick={()=>openChat(s)} style={{...styles.btn,...styles.blueBtn,fontSize:'12px',padding:'10px'}}>💬</button>
                      </>
                    ) : (
                      <button onClick={()=>handlePurchaseClick(s)} style={{width:'100%',...styles.btn,...styles.blueBtn}}>◎ Purchase</button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Create Service Modal */}
      {showCreateForm && (
        <div style={styles.modal} onClick={e=>e.target===e.currentTarget&&setShowCreateForm(false)}>
          <div style={styles.modalContent}>
            <div style={styles.modalHeader}>
              <span style={{color:'#fff',fontSize:'18px',fontWeight:'700'}}>Create Service</span>
              <button onClick={()=>setShowCreateForm(false)} style={{backgroundColor:'rgba(255,255,255,0.2)',color:'#fff',border:'none',width:'32px',height:'32px',borderRadius:'8px',cursor:'pointer',fontSize:'16px'}}>×</button>
            </div>
            <div style={{padding:'24px'}}>
              <div style={{marginBottom:'20px'}}>
                <label style={styles.label}>Service Image</label>
                <input type="file" accept="image/*" onChange={handleImageUpload} style={{...styles.input,padding:'10px'}}/>
                {newService.image && (
                  <div style={{marginTop:'10px'}}>
                    <img src={newService.image} alt="Preview" style={{width:'100%',maxHeight:'150px',objectFit:'cover',borderRadius:'8px'}}/>
                    <button onClick={()=>setNewService(prev=>({...prev,image:''}))} style={{marginTop:'8px',backgroundColor:colors.danger,color:'#fff',border:'none',padding:'6px 12px',borderRadius:'6px',cursor:'pointer',fontSize:'11px'}}>Remove</button>
                  </div>
                )}
              </div>
              <div style={{marginBottom:'20px'}}>
                <label style={styles.label}>Service Title</label>
                <input value={newService.title} onChange={e=>setNewService({...newService,title:e.target.value})} placeholder="Enter service name..." style={styles.input}/>
              </div>
              <div style={{marginBottom:'20px'}}>
                <label style={styles.label}>Your Name</label>
                <input value={newService.seller} onChange={e=>setNewService({...newService,seller:e.target.value})} placeholder="Your display name..." style={styles.input}/>
              </div>
              <div style={{marginBottom:'20px'}}>
                <label style={styles.label}>Solana Wallet {walletAddress && <span style={{color:colors.success,fontWeight:'normal'}}>(Auto-filled)</span>}</label>
                <input value={newService.sellerWallet} onChange={e=>setNewService({...newService,sellerWallet:e.target.value})} placeholder="Wallet address for payouts..." style={styles.input}/>
              </div>
              <div style={{marginBottom:'20px'}}>
                <label style={styles.label}>Description</label>
                <textarea value={newService.description} onChange={e=>setNewService({...newService,description:e.target.value})} placeholder="Describe your service..." rows={4} style={{...styles.input,resize:'vertical'}}/>
              </div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'16px',marginBottom:'24px'}}>
                <div>
                  <label style={styles.label}>Price (◎ SOL)</label>
                  <input type="number" min="0.01" step="0.01" value={newService.price} onChange={e=>setNewService({...newService,price:e.target.value})} placeholder="0.00" style={styles.input}/>
                </div>
                <div>
                  <label style={styles.label}>Category</label>
                  <select value={newService.category} onChange={e=>setNewService({...newService,category:e.target.value})} style={{...styles.input,cursor:'pointer'}}>
                    <option value="development">Development</option>
                    <option value="design">Design</option>
                    <option value="writing">Writing</option>
                    <option value="consulting">Consulting</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
              <div style={{display:'flex',gap:'12px'}}>
                <button onClick={()=>setShowCreateForm(false)} style={{flex:1,...styles.btn,backgroundColor:colors.bgAlt,color:colors.text}}>Cancel</button>
                <button onClick={handleCreateService} style={{flex:1,...styles.btn,...styles.blueBtn}}>Create Service</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && selectedService && (
        <div style={styles.modal} onClick={e=>e.target===e.currentTarget&&paymentStatus!=='pending'&&setShowPaymentModal(false)}>
          <div style={{...styles.modalContent,maxWidth:'520px'}}>
            <div style={{...styles.modalHeader,backgroundColor:colors.warning}}>
              <span style={{color:'#fff',fontSize:'18px',fontWeight:'700'}}>Complete Payment</span>
              <button onClick={()=>setShowPaymentModal(false)} disabled={paymentStatus==='pending'} style={{backgroundColor:'rgba(255,255,255,0.2)',color:'#fff',border:'none',width:'32px',height:'32px',borderRadius:'8px',cursor:paymentStatus==='pending'?'not-allowed':'pointer',opacity:paymentStatus==='pending'?0.5:1}}>×</button>
            </div>
            <div style={{padding:'24px'}}>
              {paymentStatus==='confirmed' ? (
                <div style={{textAlign:'center',padding:'20px 0'}}>
                  <div style={{fontSize:'64px',marginBottom:'16px'}}>✅</div>
                  <h3 style={{color:colors.success,fontSize:'22px',margin:'0 0 8px'}}>Payment Confirmed!</h3>
                  <p style={{color:colors.textLight,margin:'0 0 24px'}}>You now have access to this service and can chat with the seller.</p>
                  <button onClick={()=>setShowPaymentModal(false)} style={{...styles.btn,...styles.successBtn}}>Continue</button>
                </div>
              ) : (
                <>
                  <div style={{backgroundColor:colors.bgAlt,borderRadius:'12px',padding:'20px',marginBottom:'20px'}}>
                    <div style={{color:colors.text,fontSize:'18px',fontWeight:'700',marginBottom:'8px'}}>{selectedService.title}</div>
                    <div style={{color:colors.primary,fontSize:'32px',fontWeight:'700'}}>◎ {selectedService.price} SOL</div>
                  </div>
                  <div style={{backgroundColor:'#eff6ff',border:`1px solid ${colors.primaryLight}`,borderRadius:'12px',padding:'16px',marginBottom:'20px'}}>
                    <div style={{color:colors.primary,fontSize:'13px',fontWeight:'600',marginBottom:'10px'}}>⚠ Send exact amount to escrow:</div>
                    <div onClick={()=>navigator.clipboard.writeText(ESCROW_WALLET)} style={{backgroundColor:'#fff',border:`1px solid ${colors.border}`,borderRadius:'8px',padding:'12px',cursor:'pointer',transition:'background 0.2s'}}
                      onMouseOver={e=>e.currentTarget.style.backgroundColor=colors.bgAlt}
                      onMouseOut={e=>e.currentTarget.style.backgroundColor='#fff'}>
                      <code style={{color:colors.text,fontSize:'11px',wordBreak:'break-all'}}>{ESCROW_WALLET}</code>
                    </div>
                    <p style={{color:colors.textLight,fontSize:'11px',marginTop:'10px',marginBottom:0}}>Click to copy • Funds released upon delivery</p>
                  </div>
                  <div style={{marginBottom:'20px'}}>
                    <label style={styles.label}>Transaction Signature</label>
                    <input value={txSignature} onChange={e=>setTxSignature(e.target.value)} placeholder="Paste your transaction signature..." disabled={paymentStatus==='pending'} style={{...styles.input,opacity:paymentStatus==='pending'?0.6:1}}/>
                  </div>
                  {paymentStatus==='pending' && (
                    <div style={{backgroundColor:'#fef3c7',border:`1px solid ${colors.warning}`,borderRadius:'8px',padding:'14px',marginBottom:'20px',textAlign:'center'}}>
                      <span style={{color:colors.warning,fontWeight:'600'}}>Verifying transaction<span style={{animation:'blink 0.5s infinite'}}>...</span></span>
                    </div>
                  )}
                  <div style={{display:'flex',gap:'12px'}}>
                    <button onClick={()=>setShowPaymentModal(false)} disabled={paymentStatus==='pending'} style={{flex:1,...styles.btn,backgroundColor:colors.bgAlt,color:colors.text,opacity:paymentStatus==='pending'?0.6:1,cursor:paymentStatus==='pending'?'not-allowed':'pointer'}}>Cancel</button>
                    <button onClick={handlePaymentSubmit} disabled={!txSignature.trim()||paymentStatus==='pending'} style={{flex:1,...styles.btn,...styles.blueBtn,opacity:(!txSignature.trim()||paymentStatus==='pending')?0.6:1,cursor:(!txSignature.trim()||paymentStatus==='pending')?'not-allowed':'pointer'}}>Verify Payment</button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Chat Modal */}
      {showChatModal && chatService && (
        <div style={styles.modal} onClick={e=>e.target===e.currentTarget&&setShowChatModal(false)}>
          <div style={{...styles.modalContent,maxWidth:'550px'}}>
            <div style={styles.modalHeader}>
              <div>
                <span style={{color:'#fff',fontSize:'16px',fontWeight:'700'}}>💬 Chat</span>
                <div style={{color:'#bfdbfe',fontSize:'12px',marginTop:'2px'}}>{chatService.title}</div>
              </div>
              <button onClick={()=>setShowChatModal(false)} style={{backgroundColor:'rgba(255,255,255,0.2)',color:'#fff',border:'none',width:'32px',height:'32px',borderRadius:'8px',cursor:'pointer'}}>×</button>
            </div>
            <div style={{padding:'16px'}}>
              <div style={{marginBottom:'12px',padding:'10px 14px',backgroundColor:colors.bgAlt,borderRadius:'8px',fontSize:'11px',display:'flex',justifyContent:'space-between'}}>
                <span><span style={{color:colors.textLight}}>Seller:</span> <span style={{color:colors.text,fontWeight:'600'}}>{truncate(chatService.sellerWallet)}</span></span>
                <span><span style={{color:colors.textLight}}>You:</span> <span style={{color:colors.primary,fontWeight:'600'}}>{truncate(walletAddress)}</span></span>
              </div>
              <div style={styles.chatContainer}>
                {(chatMessages[chatService.id] || []).length === 0 ? (
                  <div style={{color:colors.textLight,textAlign:'center',padding:'60px 20px'}}>No messages yet.<br/>Start the conversation!</div>
                ) : (
                  (chatMessages[chatService.id] || []).map(msg => (
                    <div key={msg.id} style={{
                      ...styles.chatMessage,
                      backgroundColor: msg.sender === walletAddress ? colors.primary : '#fff',
                      color: msg.sender === walletAddress ? '#fff' : colors.text,
                      marginLeft: msg.sender === walletAddress ? '50px' : '0',
                      marginRight: msg.sender === walletAddress ? '0' : '50px',
                      border: msg.sender === walletAddress ? 'none' : `1px solid ${colors.border}`
                    }}>
                      <div style={{fontSize:'10px',opacity:0.7,marginBottom:'4px'}}>
                        {msg.sender === chatService.sellerWallet ? '🏪 Seller' : '🛒 Buyer'} • {msg.timestamp}
                      </div>
                      <div style={{fontSize:'14px'}}>{msg.text}</div>
                    </div>
                  ))
                )}
              </div>
              <div style={styles.chatInput}>
                <input 
                  value={newMessage} 
                  onChange={e=>setNewMessage(e.target.value)} 
                  onKeyPress={e=>e.key==='Enter'&&sendMessage()}
                  placeholder="Type a message..." 
                  style={{...styles.input,flex:1}}
                />
                <button onClick={sendMessage} style={{...styles.btn,...styles.blueBtn}}>Send</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Admin Panel Modal */}
      {showAdminPanel && isAdmin && (
        <div style={styles.modal} onClick={e=>e.target===e.currentTarget&&setShowAdminPanel(false)}>
          <div style={{...styles.modalContent,maxWidth:'800px'}}>
            <div style={{...styles.modalHeader,backgroundColor:colors.danger}}>
              <span style={{color:'#fff',fontSize:'18px',fontWeight:'700'}}>🔐 Escrow Management Panel</span>
              <button onClick={()=>setShowAdminPanel(false)} style={{backgroundColor:'rgba(255,255,255,0.2)',color:'#fff',border:'none',width:'32px',height:'32px',borderRadius:'8px',cursor:'pointer'}}>×</button>
            </div>
            <div style={{padding:'24px'}}>
              <div style={{marginBottom:'20px',padding:'14px',backgroundColor:'#fef2f2',border:`1px solid #fecaca`,borderRadius:'8px',display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:'10px'}}>
                <span style={{color:colors.danger,fontSize:'13px'}}>⚠ Escrow Admin • {truncate(walletAddress)}</span>
                <div style={{display:'flex',gap:'8px',fontSize:'11px'}}>
                  <span style={{backgroundColor:'#fef3c7',color:colors.warning,padding:'4px 10px',borderRadius:'12px',fontWeight:'600'}}>
                    {pendingPayments.filter(p=>p.status==='pending').length} Pending
                  </span>
                  <span style={{backgroundColor:'#d1fae5',color:colors.success,padding:'4px 10px',borderRadius:'12px',fontWeight:'600'}}>
                    {pendingPayments.filter(p=>p.status==='released').length} Released
                  </span>
                  <span style={{backgroundColor:'#fee2e2',color:colors.danger,padding:'4px 10px',borderRadius:'12px',fontWeight:'600'}}>
                    {pendingPayments.filter(p=>p.status==='refunded').length} Refunded
                  </span>
                </div>
              </div>
              
              {/* Tabs */}
              <div style={{display:'flex',gap:'4px',marginBottom:'20px',borderBottom:`2px solid ${colors.border}`,paddingBottom:'0'}}>
                {[
                  {id:'pending',label:'Pending',color:colors.warning,count:pendingPayments.filter(p=>p.status==='pending').length},
                  {id:'released',label:'Completed',color:colors.success,count:pendingPayments.filter(p=>p.status==='released').length},
                  {id:'refunded',label:'Refunded',color:colors.danger,count:pendingPayments.filter(p=>p.status==='refunded').length},
                  {id:'all',label:'All Payments',color:colors.primary,count:pendingPayments.length},
                  {id:'chats',label:'Chats',color:'#8b5cf6',count:Object.keys(chatMessages).filter(k=>chatMessages[k]?.length>0).length}
                ].map(tab=>(
                  <button 
                    key={tab.id}
                    onClick={()=>setAdminTab(tab.id)}
                    style={{
                      padding:'12px 20px',
                      fontSize:'13px',
                      fontWeight:'600',
                      cursor:'pointer',
                      border:'none',
                      borderBottom: adminTab===tab.id ? `3px solid ${tab.color}` : '3px solid transparent',
                      backgroundColor: adminTab===tab.id ? colors.bgAlt : 'transparent',
                      color: adminTab===tab.id ? tab.color : colors.textLight,
                      borderRadius:'8px 8px 0 0',
                      transition:'all 0.2s',
                      display:'flex',
                      alignItems:'center',
                      gap:'8px'
                    }}
                  >
                    {tab.label}
                    <span style={{
                      backgroundColor: adminTab===tab.id ? tab.color : colors.border,
                      color: adminTab===tab.id ? '#fff' : colors.textLight,
                      padding:'2px 8px',
                      borderRadius:'10px',
                      fontSize:'11px'
                    }}>{tab.count}</span>
                  </button>
                ))}
              </div>
              
              /* Payment List or Chats */}
              {adminTab === 'chats' ? (
                /* Chats View */
                <div>
                  {Object.keys(chatMessages).filter(k => chatMessages[k]?.length > 0).length === 0 ? (
                    <div style={{color:colors.textLight,textAlign:'center',padding:'60px',backgroundColor:colors.bgAlt,borderRadius:'12px'}}>
                      <div style={{fontSize:'40px',marginBottom:'12px'}}>💬</div>
                      No chat conversations yet.
                    </div>
                  ) : (
                    <div style={{maxHeight:'500px',overflowY:'auto'}}>
                      {Object.keys(chatMessages).filter(k => chatMessages[k]?.length > 0).map(serviceId => {
                        const service = services.find(s => s.id === parseInt(serviceId));
                        const messages = chatMessages[serviceId] || [];
                        return (
                          <div key={serviceId} style={{
                            backgroundColor:'#fff',
                            border:`1px solid ${colors.border}`,
                            borderRadius:'12px',
                            marginBottom:'16px',
                            overflow:'hidden'
                          }}>
                            <div style={{
                              backgroundColor:'#8b5cf6',
                              padding:'14px 20px',
                              display:'flex',
                              justifyContent:'space-between',
                              alignItems:'center'
                            }}>
                              <div>
                                <div style={{color:'#fff',fontWeight:'700',fontSize:'15px'}}>{service?.title || `Service #${serviceId}`}</div>
                                <div style={{color:'rgba(255,255,255,0.7)',fontSize:'11px',marginTop:'2px'}}>
                                  {messages.length} message{messages.length !== 1 ? 's' : ''}
                                </div>
                              </div>
                              {service && (
                                <div style={{textAlign:'right'}}>
                                  <div style={{color:'rgba(255,255,255,0.7)',fontSize:'10px'}}>Seller</div>
                                  <div style={{color:'#fff',fontSize:'11px'}}>{truncate(service.sellerWallet)}</div>
                                </div>
                              )}
                            </div>
                            <div style={{padding:'16px',maxHeight:'300px',overflowY:'auto',backgroundColor:colors.bgAlt}}>
                              {messages.map(msg => (
                                <div key={msg.id} style={{
                                  marginBottom:'12px',
                                  padding:'12px 16px',
                                  borderRadius:'12px',
                                  backgroundColor: msg.sender === service?.sellerWallet ? '#fff' : colors.primary,
                                  color: msg.sender === service?.sellerWallet ? colors.text : '#fff',
                                  marginLeft: msg.sender === service?.sellerWallet ? '0' : '40px',
                                  marginRight: msg.sender === service?.sellerWallet ? '40px' : '0',
                                  border: msg.sender === service?.sellerWallet ? `1px solid ${colors.border}` : 'none'
                                }}>
                                  <div style={{fontSize:'10px',opacity:0.7,marginBottom:'6px',display:'flex',justifyContent:'space-between'}}>
                                    <span>{msg.sender === service?.sellerWallet ? '🏪 Seller' : '🛒 Buyer'}</span>
                                    <span>{msg.timestamp}</span>
                                  </div>
                                  <div style={{fontSize:'14px'}}>{msg.text}</div>
                                  <div style={{fontSize:'9px',opacity:0.5,marginTop:'6px',wordBreak:'break-all'}}>{msg.sender}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ) : (
              /* Payment List */
              (() => {
                const filtered = adminTab === 'all' 
                  ? pendingPayments 
                  : pendingPayments.filter(p => p.status === adminTab);
                
                if (filtered.length === 0) {
                  return (
                    <div style={{color:colors.textLight,textAlign:'center',padding:'60px',backgroundColor:colors.bgAlt,borderRadius:'12px'}}>
                      <div style={{fontSize:'40px',marginBottom:'12px'}}>
                        {adminTab === 'pending' ? '⏳' : adminTab === 'released' ? '✅' : adminTab === 'refunded' ? '↩️' : '📋'}
                      </div>
                      No {adminTab === 'all' ? '' : adminTab} payments found.
                    </div>
                  );
                }
                
                return (
                  <div style={{maxHeight:'400px',overflowY:'auto'}}>
                    {filtered.map(p => (
                      <div key={p.id} style={{
                        backgroundColor:'#fff',
                        border:`2px solid ${p.status==='pending'?colors.warning:p.status==='released'?colors.success:colors.danger}`,
                        borderRadius:'12px',
                        padding:'20px',
                        marginBottom:'12px'
                      }}>
                        <div style={{display:'flex',justifyContent:'space-between',marginBottom:'12px',alignItems:'flex-start'}}>
                          <div>
                            <span style={{color:colors.text,fontSize:'16px',fontWeight:'700',display:'block'}}>{p.serviceTitle}</span>
                            <span style={{color:colors.textLight,fontSize:'11px'}}>ID: {p.serviceId}</span>
                          </div>
                          <span style={{
                            backgroundColor: p.status==='pending'?'#fef3c7':p.status==='released'?'#d1fae5':'#fee2e2',
                            color: p.status==='pending'?colors.warning:p.status==='released'?colors.success:colors.danger,
                            padding:'6px 14px',
                            borderRadius:'20px',
                            fontSize:'11px',
                            fontWeight:'700',
                            textTransform:'uppercase',
                            letterSpacing:'0.5px'
                          }}>
                            {p.status === 'released' ? '✓ Completed' : p.status === 'refunded' ? '↩ Refunded' : '⏳ Pending'}
                          </span>
                        </div>
                        <div style={{color:colors.primary,fontSize:'28px',fontWeight:'700',marginBottom:'16px'}}>◎ {p.amount} SOL</div>
                        
                        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px',marginBottom:'12px'}}>
                          <div style={{backgroundColor:colors.bgAlt,padding:'12px',borderRadius:'8px'}}>
                            <div style={{fontSize:'10px',color:colors.textLight,textTransform:'uppercase',marginBottom:'4px'}}>Buyer</div>
                            <div style={{fontSize:'12px',color:colors.text,fontWeight:'600',wordBreak:'break-all'}}>{p.buyerWallet}</div>
                          </div>
                          <div style={{backgroundColor:colors.bgAlt,padding:'12px',borderRadius:'8px'}}>
                            <div style={{fontSize:'10px',color:colors.textLight,textTransform:'uppercase',marginBottom:'4px'}}>Seller</div>
                            <div style={{fontSize:'12px',color:colors.text,fontWeight:'600',wordBreak:'break-all'}}>{p.sellerWallet}</div>
                          </div>
                        </div>
                        
                        <div style={{backgroundColor:colors.bgAlt,padding:'12px',borderRadius:'8px',marginBottom:'12px'}}>
                          <div style={{fontSize:'10px',color:colors.textLight,textTransform:'uppercase',marginBottom:'4px'}}>Transaction Signature</div>
                          <div style={{fontSize:'11px',color:colors.primary,fontWeight:'600',wordBreak:'break-all'}}>{p.txSignature}</div>
                        </div>
                        
                        <div style={{fontSize:'11px',color:colors.textLight,marginBottom:'16px'}}>
                          📅 {p.createdAt}
                        </div>
                        
                        {p.status === 'pending' && (
                          <div style={{display:'flex',gap:'10px'}}>
                            <button onClick={()=>releasePayment(p.id)} style={{flex:1,...styles.btn,...styles.successBtn,fontSize:'13px',padding:'12px'}}>✓ Release to Seller</button>
                            <button onClick={()=>refundPayment(p.id)} style={{flex:1,...styles.btn,...styles.dangerBtn,fontSize:'13px',padding:'12px'}}>✕ Refund Buyer</button>
                          </div>
                        )}
                        
                        {p.status === 'released' && (
                          <div style={{backgroundColor:'#d1fae5',padding:'12px',borderRadius:'8px',textAlign:'center'}}>
                            <span style={{color:colors.success,fontWeight:'600',fontSize:'13px'}}>✓ Funds released to seller</span>
                          </div>
                        )}
                        
                        {p.status === 'refunded' && (
                          <div style={{backgroundColor:'#fee2e2',padding:'12px',borderRadius:'8px',textAlign:'center'}}>
                            <span style={{color:colors.danger,fontWeight:'600',fontSize:'13px'}}>↩ Funds refunded to buyer</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                );
              })()
              )}
              
              {/* Summary Stats */}
              {adminTab !== 'chats' && (
              <div style={{marginTop:'24px',padding:'20px',backgroundColor:colors.bgAlt,borderRadius:'12px',display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'16px'}}>
                <div style={{textAlign:'center'}}>
                  <div style={{fontSize:'24px',fontWeight:'700',color:colors.warning}}>
                    ◎ {pendingPayments.filter(p=>p.status==='pending').reduce((sum,p)=>sum+parseFloat(p.amount),0).toFixed(2)}
                  </div>
                  <div style={{fontSize:'11px',color:colors.textLight,textTransform:'uppercase'}}>Pending</div>
                </div>
                <div style={{textAlign:'center'}}>
                  <div style={{fontSize:'24px',fontWeight:'700',color:colors.success}}>
                    ◎ {pendingPayments.filter(p=>p.status==='released').reduce((sum,p)=>sum+parseFloat(p.amount),0).toFixed(2)}
                  </div>
                  <div style={{fontSize:'11px',color:colors.textLight,textTransform:'uppercase'}}>Released</div>
                </div>
                <div style={{textAlign:'center'}}>
                  <div style={{fontSize:'24px',fontWeight:'700',color:colors.danger}}>
                    ◎ {pendingPayments.filter(p=>p.status==='refunded').reduce((sum,p)=>sum+parseFloat(p.amount),0).toFixed(2)}
                  </div>
                  <div style={{fontSize:'11px',color:colors.textLight,textTransform:'uppercase'}}>Refunded</div>
                </div>
              </div>
              )}
            </div>
          </div>
        </div>
      )}

      <footer style={styles.footer}>
        <p style={{color:'#bfdbfe',fontSize:'12px',margin:'0 0 8px'}}>Solana Service Marketplace</p>
        <p style={{color:'#fff',fontSize:'16px',fontWeight:'700',margin:'0 0 8px'}}>◎ QUID</p>
        <p style={{color:'#93c5fd',fontSize:'11px',margin:0}}>Escrow: {truncate(ESCROW_WALLET)}</p>
      </footer>
    </div>
  );
}